/**
 * Market Sentiment System
 * Simulates a crypto market price using random walk + momentum + mean reversion.
 * Market price maps to earningsMultiplier affecting CPS and click power.
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import { MARKET_CONFIG, MARKET_NEWS, MANIPULATIONS, GLOBAL_MANIP_SCALE, PUMP_DUMP_COIN_CRASH } from '../config/market.js';
import { getCoinConfig } from '../config/coins.js';

function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

function priceToMultiplier(price: number): number {
  const { minPrice, maxPrice, multiplierMin, multiplierMax } = MARKET_CONFIG;
  const t = (price - minPrice) / (maxPrice - minPrice);
  return multiplierMin + clamp(t, 0, 1) * (multiplierMax - multiplierMin);
}

export const marketSentimentSystem: TickSystem<GameState, GameAction> = {
  name: 'marketSentiment',

  update(state) {
    if (!state.isRunning) return state;
    if (!state.market) return state;

    const now = Date.now();
    const market = { ...state.market };
    let changed = false;

    // Check if enough time passed for a price tick
    if (now - market.lastTickTime >= MARKET_CONFIG.tickInterval) {
      market.lastTickTime = now;
      changed = true;

      // === Calculate forces ===
      // 1. Noise (random)
      const noise = (Math.random() - 0.5) * 2 * MARKET_CONFIG.noiseScale;

      // 2. Mean reversion
      const meanReversion = (MARKET_CONFIG.meanReversionTarget - market.price) * MARKET_CONFIG.meanReversionStrength;

      // 3. News impact
      let newsImpact = 0;
      if (market.activeNews && now < market.activeNews.expiresAt) {
        const newsConfig = MARKET_NEWS.find(n => n.id === market.activeNews!.id);
        if (newsConfig) {
          const remaining = (market.activeNews.expiresAt - now) / MARKET_CONFIG.newsDuration;
          newsImpact = newsConfig.impact * remaining * 0.3;
        }
      }

      // 4. Manipulation impact (v2.0: reduced - main effect targets the exchange coin)
      let manipImpact = 0;
      if (market.activeManipulation && now < market.activeManipulation.expiresAt) {
        const remaining = (market.activeManipulation.expiresAt - now) /
          (MANIPULATIONS.find(m => m.id === market.activeManipulation!.id)?.duration || 15000);
        manipImpact = market.activeManipulation.impact * remaining * 0.4 * GLOBAL_MANIP_SCALE;
      }

      // 5. Special event force
      let specialForce = 0;
      if (market.specialEvent && now < market.specialEvent.expiresAt) {
        if (market.specialEvent.type === 'crash') {
          specialForce = (MARKET_CONFIG.crashPrice - market.price) * 0.15;
        } else {
          specialForce = (MARKET_CONFIG.boomPrice - market.price) * 0.15;
        }
      }

      // === Update momentum ===
      const newMomentum = market.momentum * MARKET_CONFIG.momentumDecay
        + noise + meanReversion + newsImpact + manipImpact + specialForce;

      market.momentum = clamp(newMomentum, -20, 20);

      // === Update price ===
      market.price = clamp(
        market.price + market.momentum,
        MARKET_CONFIG.minPrice,
        MARKET_CONFIG.maxPrice
      );

      // === Update multiplier ===
      market.earningsMultiplier = priceToMultiplier(market.price);

      // === Update price history ===
      market.priceHistory = [...market.priceHistory, market.price];
      if (market.priceHistory.length > MARKET_CONFIG.priceHistoryLength) {
        market.priceHistory = market.priceHistory.slice(-MARKET_CONFIG.priceHistoryLength);
      }
    }

    // === Expire news ===
    if (market.activeNews && now >= market.activeNews.expiresAt) {
      market.activeNews = null;
      changed = true;
    }

    // === Spawn new news ===
    if (!market.activeNews && now >= market.nextNewsTime) {
      const newsPool = MARKET_NEWS;
      const picked = newsPool[Math.floor(Math.random() * newsPool.length)];
      market.activeNews = {
        id: picked.id,
        expiresAt: now + MARKET_CONFIG.newsDuration,
      };
      market.nextNewsTime = now + MARKET_CONFIG.newsMinInterval +
        Math.random() * (MARKET_CONFIG.newsMaxInterval - MARKET_CONFIG.newsMinInterval);
      changed = true;

      // Check for special event
      if (Math.random() < MARKET_CONFIG.specialEventChance) {
        const type = Math.random() < 0.5 ? 'crash' : 'boom';
        market.specialEvent = {
          type,
          expiresAt: now + MARKET_CONFIG.specialDuration,
        };
      }
    }

    // === Expire manipulation ===
    let crashCoinId: string | null = null;
    if (market.activeManipulation && now >= market.activeManipulation.expiresAt) {
      const manipConfig = MANIPULATIONS.find(m => m.id === market.activeManipulation!.id);
      if (manipConfig && manipConfig.id === 'pump_dump') {
        market.specialEvent = {
          type: 'crash',
          expiresAt: now + MARKET_CONFIG.specialDuration,
        };
        market.momentum = -15;
        // v2.0: the targeted coin crashes too (guaranteed dump after the pump)
        crashCoinId = market.activeManipulation.coinId ?? null;
      }
      market.activeManipulation = null;
      changed = true;
    }

    // === Expire special event ===
    if (market.specialEvent && now >= market.specialEvent.expiresAt) {
      market.specialEvent = null;
      changed = true;
    }

    if (!changed) return state;

    // Apply the one-shot coin crash from an expired pump_dump
    const crashCfg = crashCoinId ? getCoinConfig(crashCoinId) : undefined;
    const crashCoin = crashCoinId ? state.exchange?.coins[crashCoinId] : undefined;
    if (crashCoinId && crashCfg && crashCoin) {
      return {
        ...state,
        market,
        exchange: {
          ...state.exchange,
          coins: {
            ...state.exchange.coins,
            [crashCoinId]: {
              ...crashCoin,
              momentum: -crashCfg.volatility * PUMP_DUMP_COIN_CRASH,
            },
          },
        },
      };
    }

    return { ...state, market };
  }
};
